import { ChangeRouteParams } from '../schema/ChangeRouteParams';
import { getToRoute } from '../utils/getToRoute';
import { RouteConfig } from '../schema/RouteConfig';
import { RouteState } from '../schema/RouteStateContext.types';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRouteStateDispatch } from '../context/RouteStateContext';

/**
 * Hook which returns the utilities for changing the route
 * The template is for the type of the state
 */
export const useChangeRoute = <T extends {}>(): ChangeRouteParams<T> => {
    const navigate = useNavigate();
    const routeStateDispatch = useRouteStateDispatch();

    const pushRoute = useCallback(
        (newPath: string, config?: RouteConfig<T>) => {
            routeStateDispatch?.(config?.routeState);
            navigate(getToRoute(newPath), { state: config?.state });
        },
        [routeStateDispatch, navigate]
    );

    const replaceRoute = useCallback(
        (newPath: string, config?: RouteConfig<T>) => {
            routeStateDispatch?.(config?.routeState);
            navigate(getToRoute(newPath), { replace: true, state: config?.state });
        },
        [routeStateDispatch, navigate]
    );

    const goBack = useCallback(
        (steps: number = 1, routeState?: RouteState) => {
            routeStateDispatch?.(routeState);
            navigate(-steps);
        },
        [routeStateDispatch, navigate]
    );

    return { goBack, pushRoute, replaceRoute };
};
